import React, { useState, useEffect } from 'react';
import { Table, Space, Row, Col, Button, DatePicker, Input, Form, message, Alert } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { getConsultantBillingLoans, updateConsultantBilling, updateTemporaryBilling } from '../../services/loanService';
import locale from 'antd/es/date-picker/locale/de_DE';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Page from '../../components/layout/Page';
import MetricCard from '../../components/MetricCard';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const ConsultantBillingAdmin = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const navigate = useNavigate();

    const [consultants, setConsultants] = useState([]);
    const [selectedConsultantId, setSelectedConsultantId] = useState(null);
    const [isConsultantSelected, setIsConsultantSelected] = useState(false);
    const [selectedConsultant, setSelectedConsultant] = useState(null);

    const [primaryLoans, setPrimaryLoans] = useState([]);
    const [temporaryLoans, setTemporaryLoans] = useState([]);
    const [withdrawalLoans, setWithdrawalLoans] = useState([]);
    const [selectedPrimaryLoans, setSelectedPrimaryLoans] = useState([]);
    const [selectedTemporaryLoans, setSelectedTemporaryLoans] = useState([]);
    const [selectedWithdrawalLoans, setSelectedWithdrawalLoans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [metrics, setMetrics] = useState({
        totalPrimaryShare: 0,
        totalExtraBonus: 0,
        totalTemporaryShare: 0,
        totalWithdrawalShare: 0,
        totalSum: 0
    });
    const [hasRequiredFields, setHasRequiredFields] = useState(true);

    const primaryColumns = [
        {
            title: t('loan.fields.primaryConsultant'),
            key: 'primary_consultant',
            render: (_, record) => `${record.primary_consultant?.first_name} ${record.primary_consultant?.last_name}`
        },      
        {
            title: t('loan.fields.temporaryConsultant'),
            key: 'secondary_consultant',
            render: (_, record) => record.secondary_consultant ? 
                `${record.secondary_consultant.first_name} ${record.secondary_consultant.last_name}` : ''
        },      
        {
            title: t('loan.fields.customer'),
            key: 'customer',
            render: (_, record) => record.customer?.name || ''
        },
        {
            title: t('loan.fields.processNumber'),
            dataIndex: 'process_number',
            key: 'process_number',
            fixed: 'left',
            width: 150,
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },
        {
            title: t('loan.fields.invoice_comment'),
            dataIndex: 'invoice_comment',
            key: 'invoice_comment',
            fixed: 'left',
            width: 150,
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },     
        {
            title: t('loan.fields.amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => amount ? `${Number(amount).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },
        {
            title: t('Provision'),
            dataIndex: 'provision',
            key: 'provision',
            render: (provision) => provision ? `${Number(provision).toLocaleString('de-DE', { minimumFractionDigits: 4 })} %` : '-',
            align: 'right'
        },  
        {
            title: t('loan.fields.extraBonus'),
            key: 'extra_bonus',
            render: (_, record) => record.commission_info?.primary_share ? 
                `${Number(record.commission_info.extra_bonus).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },          
        {
            title: t('loan.fields.primaryShare'),
            key: 'commission',
            render: (_, record) => record.commission_info?.primary_share ? 
                `${Number(record.commission_info.primary_share).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        }
    ];

    const temporaryColumns = [
        {
            title: t('loan.fields.primaryConsultant'),
            key: 'primary_consultant',
            render: (_, record) => `${record.primary_consultant?.first_name} ${record.primary_consultant?.last_name}`
        },      
        {
            title: t('loan.fields.temporaryConsultant'),
            key: 'secondary_consultant',
            render: (_, record) => record.secondary_consultant ? 
                `${record.secondary_consultant.first_name} ${record.secondary_consultant.last_name}` : ''
        },      
        {
            title: t('loan.fields.customer'),
            key: 'customer',
            render: (_, record) => record.customer?.name || ''
        },
        {
            title: t('loan.fields.processNumber'),
            dataIndex: 'process_number',
            key: 'process_number',
            fixed: 'left',
            width: 150,
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },
        {
            title: t('loan.fields.invoice_comment'),
            dataIndex: 'invoice_comment',
            key: 'invoice_comment',
            fixed: 'left',
            width: 150,
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },           
        {
            title: t('loan.fields.amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => amount ? `${Number(amount).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },
        {
            title: t('loan.fields.amount'),
            dataIndex: 'evaluation_amount',
            key: 'evaluation_amount',
            render: (evaluation_amount) => evaluation_amount ? `${Number(evaluation_amount).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },        
        {
            title: t('Provision'),
            dataIndex: 'provision',
            key: 'provision',
            render: (provision) => provision ? `${Number(provision).toLocaleString('de-DE', { minimumFractionDigits: 4 })} %` : '-',
            align: 'right'
        },             
        {
            title: t('loan.fields.temporaryShare'),
            key: 'temporary_share',
            render: (_, record) => record.temporary_consultant_share ? 
                `${Number(record.temporary_consultant_share).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        }
    ];

    const withdrawalColumns = [
        {
            title: t('loan.fields.primaryConsultant'),
            key: 'primary_consultant',
            render: (_, record) => `${record.primary_consultant?.first_name} ${record.primary_consultant?.last_name}`
        },      
        {
            title: t('loan.fields.temporaryConsultant'),
            key: 'secondary_consultant',
            render: (_, record) => record.secondary_consultant ? 
                `${record.secondary_consultant.first_name} ${record.secondary_consultant.last_name}` : ''
        },      
        {
            title: t('loan.fields.customer'),
            key: 'customer',
            render: (_, record) => record.customer?.name || ''
        },
        {
            title: t('loan.fields.processNumber'),
            dataIndex: 'process_number',
            key: 'process_number',
            fixed: 'left',
            width: 150,
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },
        {
            title: t('loan.fields.invoice_comment'),
            dataIndex: 'invoice_comment',
            key: 'invoice_comment',
            fixed: 'left',
            width: 150,
            render: (text, record) => <Link to={`/loans/${record.id}`}>{text}</Link>
        },  
        {
            title: t('loan.fields.signatureDate'),
            dataIndex: 'signature_date',
            key: 'signature_date',
            render: (signature_date) => signature_date ? moment(signature_date).format('DD.MM.YYYY') : '-'
        },        
        {
            title: t('loan.fields.amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => amount ? `${Number(amount).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },
        {
            title: t('Provision'),
            dataIndex: 'provision',
            key: 'provision',
            render: (provision) => provision ? `${Number(provision).toLocaleString('de-DE', { minimumFractionDigits: 4 })} %` : '-',
            align: 'right'
        },  
        {
            title: t('loan.fields.extraBonus'),
            key: 'extra_bonus',
            render: (_, record) => record.commission_info?.primary_share ? 
                `${Number(record.commission_info.extra_bonus).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        },          
        {
            title: t('loan.fields.primaryShare'),
            key: 'commission',
            render: (_, record) => record.commission_info?.primary_share ? 
                `${Number(record.commission_info.primary_share).toLocaleString('de-DE')} €` : '-',
            align: 'right'
        }
    ];

    const fetchConsultants = async () => {
        setLoading(true);
        try {
            const response = await api.get('/consultants/');
            setConsultants(response.data);
        } catch (error) {
            console.error('Error fetching consultants:', error);
            message.error('Fehler beim Laden der Berater');
        } finally {
            setLoading(false);
        }
    };

    const selectConsultant = (consultant) => {
        setSelectedConsultantId(consultant.id);
        setSelectedConsultant(consultant);
        setIsConsultantSelected(true);
    };

    const resetConsultant = () => {
        setSelectedConsultantId(null);
        setSelectedConsultant(null);
        setIsConsultantSelected(false);
        setSelectedPrimaryLoans([]);
        setSelectedTemporaryLoans([]);
        setSelectedWithdrawalLoans([]);
        setPrimaryLoans([]);
        setTemporaryLoans([]);
        setWithdrawalLoans([]);
        setMetrics({
            totalPrimaryShare: 0,
            totalExtraBonus: 0,
            totalTemporaryShare: 0,
            totalWithdrawalShare: 0,
            totalSum: 0
        });
    };

    const calculateMetrics = (primary, temporary, withdrawal) => {
        const selectedPrimaryData = primary.filter(loan => selectedPrimaryLoans.includes(loan.id));
        const selectedTemporaryData = temporary.filter(loan => selectedTemporaryLoans.includes(loan.id));
        const selectedWithdrawalData = withdrawal.filter(loan => selectedWithdrawalLoans.includes(loan.id));
        
        const totalPrimaryShare = selectedPrimaryData.reduce((sum, loan) => {
            const primaryShare = parseFloat(loan.commission_info?.primary_share) || 0;
            return sum + primaryShare;
        }, 0);
        const totalExtraBonus = selectedPrimaryData.reduce((sum, loan) => {
            const extraBonus = parseFloat(loan.commission_info?.extra_bonus) || 0;
            return sum + extraBonus;
        }, 0);
        const totalTemporaryShare = selectedTemporaryData.reduce((sum, loan) => {
            const temporaryShare = parseFloat(loan.temporary_consultant_share) || 0;
            return sum + temporaryShare;
        }, 0);
        const totalWithdrawalShare = selectedWithdrawalData.reduce((sum, loan) => {
            const withdrawalShare = parseFloat(loan.commission_info?.primary_share) || 0;
            const extraBonus = parseFloat(loan.commission_info?.extra_bonus) || 0;
            const temporaryShare = parseFloat(loan.temporary_consultant_share) || 0;
            return sum + withdrawalShare + extraBonus + temporaryShare;
        }, 0);

        const totalSum = totalPrimaryShare + totalExtraBonus + totalTemporaryShare + totalWithdrawalShare;

        setMetrics({
            totalPrimaryShare,
            totalExtraBonus,
            totalTemporaryShare,
            totalWithdrawalShare,
            totalSum
        });
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            if (selectedConsultantId) {
                const loansData = await getConsultantBillingLoans(selectedConsultantId);
                
                const primary = loansData.filter(loan => loan.consultant_billable_status === 'primary');
                const temporary = loansData.filter(loan => loan.consultant_billable_status === 'temporary');
                const withdrawal = loansData.filter(loan => loan.consultant_billable_status === 'withdrawal');
                
                setPrimaryLoans(primary);
                setTemporaryLoans(temporary);
                setWithdrawalLoans(withdrawal);

                calculateMetrics(primary, temporary, withdrawal);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConsultants();
    }, []);

    useEffect(() => {
        if (selectedConsultantId) {
            fetchData();
        }
    }, [selectedConsultantId]);

    useEffect(() => {
        if (primaryLoans.length > 0 || temporaryLoans.length > 0 || withdrawalLoans.length > 0) {
            calculateMetrics(primaryLoans, temporaryLoans, withdrawalLoans);
        }
    }, [selectedPrimaryLoans, selectedTemporaryLoans, selectedWithdrawalLoans]);

    useEffect(() => {
        const checkRequiredFields = async () => {
            if (selectedConsultantId) {
                try {
                    const response = await api.get('/settings/');
                    const settings = response.data;
                    
                    const requiredFields = ['street', 'zip_code', 'city', 'iban', 'tax_number'];
                    const missingFields = requiredFields.some(field => 
                        !settings[field] || settings[field].trim() === ''
                    );
                    
                    setHasRequiredFields(!missingFields);
                } catch (error) {
                    console.error('Error checking required fields:', error);
                    setHasRequiredFields(false);
                }
            }
        };

        checkRequiredFields();
    }, [selectedConsultantId]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const { invoiceDate, invoiceNumber } = values;

            const billingData = {
                billingDate: invoiceDate.format('YYYY-MM-DD'),
                billingNumber: invoiceNumber
            };

            const primaryUpdates = selectedPrimaryLoans.map(loanId =>
                updateConsultantBilling(loanId, billingData)
            );

            const temporaryUpdates = selectedTemporaryLoans.map(loanId =>
                updateTemporaryBilling(loanId, billingData)
            );

            const withdrawalUpdates = selectedWithdrawalLoans.map(loanId =>
                updateConsultantBilling(loanId, billingData)
            );

            await Promise.all([...primaryUpdates, ...temporaryUpdates, ...withdrawalUpdates]);
            
            message.success('Beraterabrechnung erfolgreich gespeichert');
            
            form.resetFields();
            setSelectedPrimaryLoans([]);
            setSelectedTemporaryLoans([]);
            setSelectedWithdrawalLoans([]);
            fetchData();
        } catch (error) {
            console.error('Error updating consultant billing:', error);
            message.error('Fehler beim Speichern der Beraterabrechnung');
        }
    };

    const metricCards = [
        {
            title: t('billing.metrics.sumPrimaryShare'),
            value: `${Number(metrics.totalPrimaryShare).toLocaleString('de-DE')} €`,
            precision: 2
        },
        {
            title: t('billing.metrics.sumTemporaryShare'),
            value: `${Number(metrics.totalTemporaryShare).toLocaleString('de-DE')} €`,
            precision: 2
        },
        {
            title: t('billing.metrics.sumExtraBonus'),
            value: `${Number(metrics.totalExtraBonus).toLocaleString('de-DE')} €`,
            precision: 2
        },
        {
            title: t('billing.metrics.sumWithdrawal'),
            value: `${Number(metrics.totalWithdrawalShare).toLocaleString('de-DE')} €`,
            precision: 2
        },
        {
            title: t('billing.metrics.sumTotal'),
            value: `${Number(metrics.totalSum).toLocaleString('de-DE')} €`,
            precision: 2
        }        
    ];

    const handlePrimaryLoansSelect = (selectedRowKeys) => {
        setSelectedPrimaryLoans(selectedRowKeys);
    };

    const handleTemporaryLoansSelect = (selectedRowKeys) => {
        setSelectedTemporaryLoans(selectedRowKeys);
    };

    const handleWithdrawalLoansSelect = (selectedRowKeys) => {
        setSelectedWithdrawalLoans(selectedRowKeys);
    };

    const ConsultantSelectionTable = () => (
        <div>
            <Table
                dataSource={consultants}
                rowKey="id"
                loading={loading}
                columns={[
                    {
                        title: 'Name',
                        key: 'name',
                        sorter: (a, b) => {
                            const lastNameResult = (a.last_name || '').localeCompare(b.last_name || '');
                            if (lastNameResult !== 0) return lastNameResult;
                            return (a.first_name || '').localeCompare(b.first_name || '');
                        },
                        render: (_, record) => `${record.first_name || ''} ${record.last_name || ''}`
                    },
                    {
                        title: 'Aktion',
                        key: 'action',
                        render: (_, record) => (
                            <Button 
                                type="primary" 
                                onClick={() => selectConsultant(record)}
                            >
                                Auswählen
                            </Button>
                        )
                    }
                ]}
            />
        </div>
    );

    const headerExtra = (
        <Row gutter={[8, 8]} align="middle">
            {isConsultantSelected && (
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Button onClick={resetConsultant}>
                        Zurück zur Beraterliste
                    </Button>
                </Col>
            )}
            <Col xs={24} sm={24} md={12} lg={8}>
                <Button type="primary" onClick={() => navigate('/accountant/consultant-billing-admin-overview')}>
                    {t('billing.billingOverview') || 'Abrechnungsübersicht'}
                </Button>
            </Col>
        </Row>
    );

    return (
        <Page 
            title={isConsultantSelected 
                ? `${t('consultantBilling.title') || 'Beraterabrechnung'}: ${selectedConsultant?.first_name || ''} ${selectedConsultant?.last_name || ''}` 
                : t('consultantBilling.selectConsultantTitle') || 'Berater auswählen'}
            subtitle={isConsultantSelected ? t('billing.subtitle') || '' : t('consultantBilling.selectConsultantSubtitle') || 'Wählen Sie einen Berater aus'}
            headerExtra={headerExtra}
        >
            {!isConsultantSelected ? (
                <ConsultantSelectionTable />
            ) : (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {!hasRequiredFields && (
                        <Alert
                            message={t('billing.missingFields.title')}
                            description={
                                <span>
                                    {t('billing.missingFields.description')}{' '}
                                    <Link to="/settings">{t('billing.missingFields.link')}</Link>
                                </span>
                            }
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Row gutter={[16, 16]}>
                        {metricCards.map((card, index) => (
                            <Col key={index} xs={24} sm={12} md={4}>
                                <MetricCard {...card} />
                            </Col>
                        ))}
                    </Row>

                    <Form
                        form={form}
                        layout="vertical"
                        style={{ width: '100%' }}
                    >
                        <Row gutter={24}>
                            <Col xs={24} sm={24} md={8} lg={4}>
                                <Form.Item
                                    name="invoiceDate"
                                    label={t('common.invoiceDate')}
                                    rules={[{ required: true, message: 'Bitte Rechnungsdatum eingeben' }]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD.MM.YYYY"
                                        locale={locale}
                                        showToday={false}
                                        allowClear={true}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={8} lg={4}>
                                <Form.Item
                                    name="invoiceNumber"
                                    label={t('common.invoiceNumber')}
                                    rules={[{ required: true, message: 'Bitte Rechnungsnummer eingeben' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={8} lg={4}>
                                <Button
                                    style={{ marginTop: 30, width: '100%' }}
                                    type="primary"
                                    onClick={handleSubmit}
                                    disabled={selectedPrimaryLoans.length === 0 && selectedTemporaryLoans.length === 0 && selectedWithdrawalLoans.length === 0}
                                >
                                    {t('billing.createInvoice')}
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    <div style={{ marginTop: 24 }}>
                        <h2>{t('billing.primary_consultant_loans')}</h2>
                        <div style={{ 
                            width: '100%',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}>
                            <Table
                                rowSelection={{
                                    selectedRowKeys: selectedPrimaryLoans,
                                    onChange: handlePrimaryLoansSelect
                                }}
                                dataSource={primaryLoans}
                                columns={primaryColumns}
                                rowKey="id"
                                loading={loading}
                                scroll={{ x: 'max-content' }}
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: 24 }}>
                        <h2>{t('billing.temporary_consultant_loans')}</h2>
                        <div style={{ 
                            width: '100%',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}>
                            <Table
                                rowSelection={{
                                    selectedRowKeys: selectedTemporaryLoans,
                                    onChange: handleTemporaryLoansSelect
                                }}
                                dataSource={temporaryLoans}
                                columns={temporaryColumns}
                                rowKey="id"
                                loading={loading}
                                scroll={{ x: 'max-content' }}
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: 24 }}>
                        <h2>{t('billing.withdrawal_loans')}</h2>
                        <div style={{ 
                            width: '100%',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}>
                            <Table
                                rowSelection={{
                                    selectedRowKeys: selectedWithdrawalLoans,
                                    onChange: handleWithdrawalLoansSelect
                                }}
                                dataSource={withdrawalLoans}
                                columns={withdrawalColumns}
                                rowKey="id"
                                loading={loading}
                                scroll={{ x: 'max-content' }}
                            />
                        </div>
                    </div>
                </Space>    
            )}
        </Page>
    );
};

export default ConsultantBillingAdmin;
